import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import PaymentForm from '../components/payments/PaymentForm';
import './DashboardPages.css';
import './DashboardSubscription.css';
import { subscriptionsApi } from '../api/subscriptions';
import { paymentsApi } from '../api/payments';
import LoadingSpinner from '../components/LoadingSpinner';
import { s3Api } from '../api/s3';

function DashboardSubscription() {
	const {
		orgData,
		refreshOrganizationData,
		setHasActiveSubscription,
		hasActiveSubscription,
	} = useAuth();
	const location = useLocation();
	const navigate = useNavigate();
	const [selectedPlan, setSelectedPlan] = useState(null);
	const [paymentSuccess, setPaymentSuccess] = useState(false);
	const [currentSubscription, setCurrentSubscription] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [subscriptionPlans, setSubscriptionPlans] = useState([]);
	const [billingInterval, setBillingInterval] = useState('monthly');
	const [subscriptionStatus, setSubscriptionStatus] = useState(null);
	const [showUpdateOptions, setShowUpdateOptions] = useState(false);
	const [cartItems, setCartItems] = useState([]);
	const [showCart, setShowCart] = useState(false);
	const [addonSuccess, setAddonSuccess] = useState(false);
	const [showAddonConfirm, setShowAddonConfirm] = useState(false);
	const [selectedAddon, setSelectedAddon] = useState(null);
	const [successMessage, setSuccessMessage] = useState(null);
	const [storageUsage, setStorageUsage] = useState(null);
	const [loadingUsage, setLoadingUsage] = useState(false);
	const [products, setProducts] = useState([]);
	const [invoices, setInvoices] = useState([]);

	// Check for success parameter in URL (from Stripe redirect)
	useEffect(() => {
		const queryParams = new URLSearchParams(location.search);
		const success = queryParams.get('success');

		if (success === 'true' && orgData?.id) {
			// Refresh subscription data
			const refreshData = async () => {
				try {
					setLoading(true);

					// Set subscription as active immediately to update UI
					setHasActiveSubscription(true);
					// Show success message
					setSuccessMessage(
						'Your subscription has been activated successfully!'
					);
					// Remove query parameters from URL
					navigate('/dashboard/subscription', { replace: true });
				} catch (error) {
					console.error('Error refreshing subscription data:', error);
					setError(
						'Failed to update subscription status. Please refresh the page.'
					);
				} finally {
					setLoading(false);
				}
			};

			refreshData();
		}
	}, [
		location,
		navigate,
		orgData?.id,
		refreshOrganizationData,
		setHasActiveSubscription,
	]);

	// Fix the processProducts function to correctly handle the Stripe product data
	const processProducts = (products) => {
		if (!products || !Array.isArray(products)) {
			return { mainPlans: [], storageAddOns: [] };
		}

		// Group products by their base name (removing "Yearly" suffix if present)
		const groupedProducts = {};
		const storageAddOns = [];

		products.forEach((product) => {
			// Check if this is a storage add-on
			if (product.name.toLowerCase().includes('storage add-on')) {
				// Process storage add-on
				const price = product.default_price;
				if (!price) {
					return;
				}

				storageAddOns.push({
					id: price.id,
					name: product.name,
					description: product.description,
					price: price.unit_amount / 100, // Convert from cents to dollars
					billingInterval: price.recurring?.interval || 'month',
					features: [
						product.description || '50GB Additional Storage',
					],
				});
				return;
			}

			// Process subscription plans
			const isYearly = product.name.toLowerCase().includes('yearly');
			const baseName = isYearly
				? product.name.replace(' (Yearly)', '')
				: product.name;

			if (!groupedProducts[baseName]) {
				groupedProducts[baseName] = {
					baseName,
					description: product.description,
					features: extractFeaturesFromDescription(
						product.description
					),
					monthly: null,
					yearly: null,
				};
			}

			const price = product.default_price;
			if (!price) {
				return;
			}

			if (isYearly) {
				groupedProducts[baseName].yearly = {
					id: price.id,
					price: price.unit_amount / 100, // Convert from cents to dollars
				};
			} else {
				groupedProducts[baseName].monthly = {
					id: price.id,
					price: price.unit_amount / 100, // Convert from cents to dollars
				};
			}
		});

		// Convert the grouped products object to an array
		const mainPlans = Object.values(groupedProducts);

		// Sort plans by price (assuming Basic < Growth < Pro < Enterprise)
		mainPlans.sort((a, b) => {
			if (a.monthly && b.monthly) {
				return a.monthly.price - b.monthly.price;
			}
			return 0;
		});

		const result = { mainPlans, storageAddOns };
		return result;
	};

	// Helper function to extract features from description
	const extractFeaturesFromDescription = (description) => {
		if (!description) return ['Standard features'];

		// For now, just return the description as a feature
		// In a real implementation, you might parse the description or have separate features data
		return [description];
	};

	// Update the fetchData function to include more debugging
	const fetchData = async () => {
		try {
			setLoading(true);

			// Check if we have organization data
			if (!orgData?.id) {
				setLoading(false);
				return;
			}

			// Try to get the current subscription
			try {
				const subscriptionResponse =
					await subscriptionsApi.getOrganizationSubscription(
						orgData.id
					);

				if (subscriptionResponse) {
					setCurrentSubscription(subscriptionResponse);
				}
			} catch (error) {
				console.error('Error fetching subscription:', error);
				// Don't throw here, continue to fetch products
			}

			// Always fetch products, even if there's no active subscription
			try {
				const productsResponse = await paymentsApi.getProducts();

				// Process the products into plans
				const processedPlans = processProducts(productsResponse || []);
				setSubscriptionPlans(processedPlans);
				setProducts(productsResponse.products || []);
			} catch (productsError) {
				console.error('Error fetching products:', productsError);
				setProducts([]);
				setError('Failed to load available subscription plans');
			}

			// Fetch invoices only if there's an active subscription
			if (
				currentSubscription?.stripeCustomerId &&
				subscriptionsApi &&
				typeof subscriptionsApi.getInvoices === 'function'
			) {
				try {
					const invoicesResponse = await subscriptionsApi.getInvoices(
						currentSubscription.stripeCustomerId
					);
					setInvoices(invoicesResponse.invoices || []);
				} catch (invoicesError) {
					console.error('Error fetching invoices:', invoicesError);
					setInvoices([]);
				}
			}
		} catch (error) {
			console.error('Error fetching data:', error);
			setError('Failed to load subscription data');
		} finally {
			setLoading(false);
		}
	};

	// Update the useEffect to run regardless of subscription status
	useEffect(() => {
		if (orgData?.id) {
			fetchData();
		}
	}, [orgData]);

	const handlePlanSelect = (plan) => {
		// Select the plan based on the current billing interval
		const selectedPlanDetails = {
			id:
				billingInterval === 'yearly'
					? plan.yearly?.id
					: plan.monthly?.id,
			name: `${plan.baseName}${
				billingInterval === 'yearly' ? ' (Yearly)' : ''
			}`,
			price:
				billingInterval === 'yearly'
					? plan.yearly?.price
					: plan.monthly?.price,
			features: plan.features,
			description: plan.description,
			type: 'plan',
		};

		// Check if we already have a plan in the cart
		const existingPlanIndex = cartItems.findIndex(
			(item) => item.type === 'plan'
		);

		if (existingPlanIndex >= 0) {
			// Replace the existing plan
			const newCartItems = [...cartItems];
			newCartItems[existingPlanIndex] = selectedPlanDetails;
			setCartItems(newCartItems);
		} else {
			// Add the plan to cart
			setCartItems([...cartItems, selectedPlanDetails]);
		}

		setShowCart(true);
	};

	const handleStorageSelect = (addon) => {
		const addonDetails = {
			...addon,
			type: 'addon',
		};

		// Check if this addon is already in the cart
		const existingAddonIndex = cartItems.findIndex(
			(item) => item.id === addon.id
		);

		if (existingAddonIndex >= 0) {
			// Remove it if it's already there (toggle behavior)
			const newCartItems = [...cartItems];
			newCartItems.splice(existingAddonIndex, 1);
			setCartItems(newCartItems);
		} else {
			// Add it to the cart
			setCartItems([...cartItems, addonDetails]);
		}

		setShowCart(true);
	};

	const toggleBillingInterval = () => {
		setBillingInterval((prev) =>
			prev === 'monthly' ? 'yearly' : 'monthly'
		);
		// Clear selected plan when toggling
		setSelectedPlan(null);
	};

	const handleCancelSubscription = async () => {
		if (!orgData?.id || !currentSubscription) return;

		try {
			setLoading(true);
			await subscriptionsApi.cancelOrganizationSubscription(orgData.id);

			// Refresh subscription data
			const subscriptionData =
				await subscriptionsApi.getOrganizationSubscription(orgData.id);
			setCurrentSubscription(subscriptionData.subscription);

			alert(
				'Your subscription has been canceled. You will have access until the end of your billing period.'
			);
		} catch (error) {
			console.error('Error canceling subscription:', error);
			setError('Failed to cancel subscription. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	const fetchSubscriptionData = async (orgId) => {
		if (!orgId) return;

		try {
			setLoading(true);
			const subscriptionData =
				await subscriptionsApi.getOrganizationSubscription(orgId);
			setCurrentSubscription(subscriptionData.subscription);
			setSubscriptionStatus(subscriptionData);
		} catch (error) {
			console.error('Error fetching subscription data:', error);
		} finally {
			setLoading(false);
		}
	};

	// Add a function to remove items from cart
	const removeFromCart = (itemId) => {
		setCartItems(cartItems.filter((item) => item.id !== itemId));

		// If cart becomes empty, hide it
		if (cartItems.length <= 1) {
			setShowCart(false);
		}
	};

	// Update the handleAddonSelect function to offer immediate billing option
	const handleAddonSelect = async (addon, immediateCharge = false) => {
		try {
			setLoading(true);
			setError(null);

			// Add the add-on to the existing subscription
			await subscriptionsApi.addAddon(
				orgData.id,
				{
					productId: addon.id,
					addonName: addon.name,
					addonFeatures: addon.features || [],
				},
				immediateCharge
			);

			// Refresh the subscription data
			await fetchSubscriptionData(orgData.id);

			// Show success message
			setAddonSuccess(true);

			// Reset success message after 5 seconds
			setTimeout(() => {
				setAddonSuccess(false);
			}, 5000);
		} catch (error) {
			console.error('Error adding addon:', error);
			setError('Failed to add addon. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	// Function to open the confirmation dialog
	const openAddonConfirm = (addon) => {
		setSelectedAddon(addon);
		setShowAddonConfirm(true);
	};

	// Update the handleUpdateSubscription function to use the new addon API
	const handleUpdateSubscription = async () => {
		if (cartItems.length === 0 || !orgData?.id) return;

		try {
			setLoading(true);
			setError(null);

			// Find the plan in the cart (if any)
			const planItem = cartItems.find((item) => item.type === 'plan');
			// Get all addons
			const addonItems = cartItems.filter(
				(item) => item.type === 'addon'
			);

			// Check if the user has an active subscription
			const isNewSubscription =
				!currentSubscription || currentSubscription.status !== 'active';

			// If this is a new subscription or the user doesn't have an active subscription
			if (isNewSubscription) {
				// For new subscriptions, we'll use the checkout process for everything
				try {
					// Prepare all items for checkout (plan and add-ons)
					const checkoutItems = [];

					// Add the plan if selected
					if (planItem) {
						checkoutItems.push({
							priceId: planItem.id,
							quantity: 1,
							name: planItem.name,
						});
					}

					// Add any add-ons
					addonItems.forEach((addon) => {
						checkoutItems.push({
							priceId: addon.id,
							quantity: 1,
							name: addon.name,
						});
					});

					// Use the first item for the checkout session (API limitation)
					const firstItem = checkoutItems[0];

					// Call the API to create a checkout session
					const response = await paymentsApi.createCheckoutSession(
						orgData.id,
						{
							items: [
								{
									priceId: firstItem.priceId,
									quantity: 1,
									name: firstItem.name,
								},
							],
						}
					);

					// Redirect to Stripe checkout
					if (response && response.url) {
						window.location.href = response.url;
						return;
					} else {
						throw new Error('No checkout URL returned from API');
					}
				} catch (checkoutError) {
					console.error(
						'Error creating checkout session:',
						checkoutError
					);
					setError('Failed to create checkout. Please try again.');
				}
			}
			// If we have an existing subscription and a plan change
			else if (planItem) {
				// Handle plan changes with or without add-ons for existing subscriptions
				const updateData = {
					priceId: planItem.id,
					planType: planItem.name,
				};

				const response = await subscriptionsApi.updateSubscription(
					orgData.id,
					updateData
				);

				// Process add-ons separately using the new API
				if (addonItems.length > 0) {
					// Add each add-on one by one
					for (const addon of addonItems) {
						await subscriptionsApi.addAddon(orgData.id, {
							productId: addon.id,
							addonName: addon.name,
							addonFeatures: addon.features || [],
						});
					}
				}

				// Refresh subscription data after update
				await fetchSubscriptionData(orgData.id);

				// Reset UI state
				setCartItems([]);
				setShowCart(false);
				setShowUpdateOptions(false);

				// Show success message
				alert('Your subscription has been updated successfully!');
			}
			// If we have an existing subscription and only add-ons
			else if (addonItems.length > 0) {
				// For add-ons only with existing subscription, use the new addAddon API
				try {
					// Add each add-on one by one
					for (const addon of addonItems) {
						await subscriptionsApi.addAddon(orgData.id, {
							productId: addon.id,
							addonName: addon.name,
							addonFeatures: addon.features || [],
						});
					}

					// Refresh subscription data after update
					await fetchSubscriptionData(orgData.id);

					// Reset UI state
					setCartItems([]);
					setShowCart(false);

					// Show success message
					alert(
						'Add-ons have been added to your subscription successfully!'
					);
				} catch (addonError) {
					console.error('Error adding add-ons:', addonError);
					setError(
						'Failed to add add-ons to your subscription. Please try again.'
					);
				}
			}
		} catch (error) {
			console.error('Error updating subscription:', error);
			setError(
				error.message ||
					'Failed to update subscription. Please try again.'
			);
		} finally {
			setLoading(false);
		}
	};

	// Add this function to calculate total storage based on plan and add-ons
	const calculateTotalStorage = (subscription) => {
		if (!subscription) return 0;

		// Base storage based on plan type
		let baseStorage = 0;
		const planType = subscription.planType?.toLowerCase() || '';

		if (planType.includes('basic')) {
			baseStorage = 100; // 100GB for Basic
		} else if (planType.includes('growth')) {
			baseStorage = 250; // 250GB for Growth
		} else if (planType.includes('pro')) {
			baseStorage = 500; // 500GB for Pro
		} else if (planType.includes('enterprise')) {
			baseStorage = 1000; // 1TB for Enterprise
		}

		// Count storage add-ons (each gives 50GB)
		const storageAddons =
			subscription.features?.filter((feature) =>
				feature.toLowerCase().includes('storage add-on')
			) || [];

		const addonStorage = storageAddons.length * 50; // 50GB per add-on

		return baseStorage + addonStorage;
	};

	// Fix the storage usage fetching
	const fetchStorageUsage = async () => {
		try {
			// Check if we have organization data
			if (!orgData?.id) {
				setStorageUsage({
					bytes: 0,
					gigabytes: 0,
					totalSizeBytes: 0,
					totalSizeHuman: '0 B',
					isMock: true,
				});
				return;
			}

			try {
				// Try to get actual storage usage
				const size = await s3Api.getFolderSize(orgData.id);

				// Process the response data
				const storageData = {
					bytes: size.totalSizeBytes || 0,
					gigabytes: (size.totalSizeBytes || 0) / 1073741824, // Convert bytes to GB
					totalSizeBytes: size.totalSizeBytes || 0,
					totalSizeHuman: size.totalSizeHuman || '0 B',
					isMock: false,
				};

				setStorageUsage(storageData);
			} catch (error) {
				console.error('Error fetching storage usage:', error);
				// Fallback to mock data
				setStorageUsage({
					bytes: 0,
					gigabytes: 0,
					totalSizeBytes: 0,
					totalSizeHuman: '0 B',
					isMock: true,
				});
			}
		} catch (error) {
			console.error('Error fetching storage usage:', error);
			// Set default values on error
			setStorageUsage({
				bytes: 0,
				gigabytes: 0,
				totalSizeBytes: 0,
				totalSizeHuman: '0 B',
				isMock: true,
			});
		}
	};

	// Call this in useEffect
	useEffect(() => {
		if (orgData?.id) {
			fetchStorageUsage();
		}
	}, [orgData?.id]);

	// Add debugging to the showUpdateOptions toggle
	const handleShowUpdateOptions = () => {
		setShowUpdateOptions(true);
	};

	return (
		<DashboardLayout>
			<div className='dashboard-page'>
				<div className='page-header'>
					<h1>Subscription Management</h1>
				</div>

				<div className='content-section'>
					{loading ? (
						<div className='loading-indicator'>
							<LoadingSpinner />
						</div>
					) : error ? (
						<div className='error-message'>{error}</div>
					) : (
						<>
							{location.search.includes('success=true') && (
								<div className='success-message detail-card'>
									<h2>Payment Successful!</h2>
									<p>{successMessage}</p>
								</div>
							)}

							{currentSubscription && (
								<div className='detail-card'>
									<h2>Current Subscription</h2>
									<div className='subscription-details'>
										<div className='subscription-info'>
											<div className='subscription-row'>
												<span className='subscription-label'>
													Plan:
												</span>
												<span className='subscription-value'>
													{
														currentSubscription.planType
													}
												</span>
											</div>
											<div className='subscription-row'>
												<span className='subscription-label'>
													Status:
												</span>
												<span
													className={`subscription-value status status-${currentSubscription.status.toLowerCase()}`}>
													{currentSubscription.status}
												</span>
											</div>
											{currentSubscription.currentPeriodEnd && (
												<div className='subscription-row'>
													<span className='subscription-label'>
														Next billing date:
													</span>
													<span className='subscription-value'>
														{new Date(
															currentSubscription.currentPeriodEnd
														).toLocaleDateString()}
													</span>
												</div>
											)}
											{currentSubscription.status ===
												'active' && (
												<div className='subscription-row'>
													<span className='subscription-label'>
														Total Storage:
													</span>
													<span className='subscription-value'>
														{calculateTotalStorage(
															currentSubscription
														)}{' '}
														GB
													</span>
												</div>
											)}
											{currentSubscription.status ===
												'active' && (
												<div className='subscription-row'>
													<span className='subscription-label'>
														Storage Used:
													</span>
													<span className='subscription-value'>
														{loadingUsage ? (
															<span>
																Calculating...
															</span>
														) : storageUsage ? (
															<span>
																{/* Use totalSizeHuman from the API response */}
																{storageUsage.totalSizeHuman ||
																	'0 B'}
																{storageUsage.isMock &&
																	' (Estimate)'}
																{' of '}
																{calculateTotalStorage(
																	currentSubscription
																)}{' '}
																GB
																{!storageUsage.isMock &&
																	!isNaN(
																		storageUsage.gigabytes
																	) &&
																	` (${(
																		(storageUsage.gigabytes /
																			calculateTotalStorage(
																				currentSubscription
																			)) *
																		100
																	).toFixed(
																		1
																	)}%)`}
															</span>
														) : (
															<span>
																0 GB of{' '}
																{calculateTotalStorage(
																	currentSubscription
																)}{' '}
																GB (0%)
															</span>
														)}
													</span>
												</div>
											)}
											{/* Display add-ons if any */}
											{currentSubscription.features &&
												currentSubscription.features
													.length > 0 && (
													<div className='addon-list'>
														<p>
															<strong>
																Add-ons:
															</strong>
														</p>
														{(() => {
															// Group features by type and count them
															const featureCounts =
																{};

															currentSubscription.features.forEach(
																(feature) => {
																	if (
																		featureCounts[
																			feature
																		]
																	) {
																		featureCounts[
																			feature
																		]++;
																	} else {
																		featureCounts[
																			feature
																		] = 1;
																	}
																}
															);

															// Display each feature with its count
															return Object.entries(
																featureCounts
															).map(
																(
																	[
																		feature,
																		count,
																	],
																	index
																) => (
																	<div
																		key={
																			index
																		}
																		className='subscription-row'>
																		<span className='subscription-label'></span>
																		<span className='subscription-value'>
																			{
																				feature
																			}{' '}
																			{count >
																			1
																				? `x${count}`
																				: ''}
																		</span>
																	</div>
																)
															);
														})()}
													</div>
												)}
										</div>

										{currentSubscription.status ===
											'active' && (
											<div className='subscription-actions'>
												<button
													className='primary-button'
													onClick={
														handleShowUpdateOptions
													}
													style={{
														backgroundColor:
															orgData?.primaryColor ||
															'#4b95a3',
													}}>
													Update Subscription
												</button>
												<button
													className='secondary-button'
													onClick={
														handleCancelSubscription
													}>
													Cancel Subscription
												</button>
											</div>
										)}
									</div>
								</div>
							)}

							{(!currentSubscription ||
								selectedPlan !== null ||
								showUpdateOptions ||
								currentSubscription.status !== 'active') && (
								<>
									<h2>Available Plans</h2>
									{/* Add debugging info */}
									{subscriptionPlans.mainPlans?.length ===
										0 && (
										<div className='debug-info'>
											<p>
												No plans available to display.
												Check console for details.
											</p>
										</div>
									)}

									<div className='billing-toggle'>
										<span
											className={
												billingInterval === 'monthly'
													? 'active'
													: ''
											}>
											Monthly
										</span>
										<label className='switch'>
											<input
												type='checkbox'
												checked={
													billingInterval === 'yearly'
												}
												onChange={toggleBillingInterval}
											/>
											<span className='slider round'></span>
										</label>
										<span
											className={
												billingInterval === 'yearly'
													? 'active'
													: ''
											}>
											Yearly{' '}
											<span className='discount-badge'>
												2 months free!
											</span>
										</span>
									</div>

									<div className='subscription-plans'>
										{subscriptionPlans.mainPlans?.map(
											(plan) => {
												// Get the current price ID based on billing interval
												const currentPlanPriceId =
													billingInterval === 'yearly'
														? plan.yearly?.id
														: plan.monthly?.id;

												// Skip rendering if this is the current active plan with the same billing interval
												// Only skip if there's an active subscription
												if (
													hasActiveSubscription &&
													(currentSubscription?.stripePriceId ===
														currentPlanPriceId ||
														currentSubscription?.planType ===
															plan.baseName ||
														currentSubscription?.planType ===
															`${plan.baseName} (Yearly)`) &&
													selectedPlan === null &&
													!showUpdateOptions
												) {
													return null;
												}

												// Check if this plan is in the cart
												const isPlanInCart =
													cartItems.some(
														(item) =>
															item.type ===
																'plan' &&
															item.id ===
																currentPlanPriceId
													);

												return (
													<div
														key={plan.baseName}
														className={`plan-card ${
															isPlanInCart
																? 'selected'
																: ''
														}`}
														onClick={() =>
															handlePlanSelect(
																plan
															)
														}>
														<h3>{plan.baseName}</h3>
														<p className='plan-price'>
															$
															{(billingInterval ===
															'yearly'
																? plan.yearly
																		?.price
																: plan.monthly
																		?.price
															)?.toFixed(2)}
															/
															{billingInterval ===
															'yearly'
																? 'year'
																: 'month'}
														</p>
														{billingInterval ===
															'yearly' && (
															<p className='yearly-savings'>
																2 months free
																compared to
																monthly
															</p>
														)}
														<ul className='plan-features'>
															{plan.features.map(
																(
																	feature,
																	index
																) => (
																	<li
																		key={
																			index
																		}
																		style={{
																			textAlign:
																				'left',
																		}}>
																		{isPlanInCart && (
																			<span className='material-icons'>
																				check
																			</span>
																		)}
																		{
																			feature
																		}
																	</li>
																)
															)}
														</ul>
														<button
															className='select-plan-btn'
															style={{
																backgroundColor:
																	orgData?.primaryColor ||
																	'#4b95a3',
															}}
															onClick={() =>
																handlePlanSelect(
																	plan
																)
															}
															disabled={
																!plan[
																	billingInterval
																]
															}>
															{isPlanInCart
																? 'Selected'
																: 'Select Plan'}
														</button>
													</div>
												);
											}
										)}
									</div>
								</>
							)}

							{(currentSubscription?.status === 'active' ||
								selectedPlan !== null) &&
								subscriptionPlans.storageAddOns?.length > 0 && (
									<>
										<h2 style={{ marginTop: '2rem' }}>
											Storage Add-Ons
										</h2>
										<div className='subscription-plans'>
											{subscriptionPlans.storageAddOns.map(
												(addon) => {
													// Check if this addon is in the cart
													const isAddonInCart =
														cartItems.some(
															(item) =>
																item.type ===
																	'addon' &&
																item.id ===
																	addon.id
														);

													// Skip if this is the current addon
													if (
														currentSubscription?.stripePriceId ===
															addon.id &&
														selectedPlan === null
													) {
														return null;
													}

													return (
														<div
															key={addon.id}
															className={`plan-card ${
																isAddonInCart
																	? 'selected'
																	: ''
															}`}
															onClick={() =>
																openAddonConfirm(
																	addon
																)
															}>
															<h3>
																{addon.name}
															</h3>
															<p className='plan-price'>
																$
																{addon.price.toFixed(
																	2
																)}
																/
																{addon.billingInterval ===
																'yearly'
																	? 'year'
																	: 'month'}
															</p>
															<p>
																50GB Added
																Storage!
															</p>
															<ul className='plan-features'>
																{addon.features.map(
																	(
																		feature,
																		index
																	) => (
																		<li
																			key={
																				index
																			}
																			style={{
																				textAlign:
																					'left',
																			}}>
																			{isAddonInCart && (
																				<span className='material-icons'>
																					check
																				</span>
																			)}
																			{
																				feature
																			}
																		</li>
																	)
																)}
															</ul>
															<button
																className='select-plan-btn'
																style={{
																	backgroundColor:
																		orgData?.primaryColor ||
																		'#4b95a3',
																}}
																onClick={() =>
																	openAddonConfirm(
																		addon
																	)
																}>
																{isAddonInCart
																	? 'Selected'
																	: 'Select Add-On'}
															</button>
														</div>
													);
												}
											)}
										</div>
									</>
								)}

							{selectedPlan && (
								<div className='payment-section detail-card'>
									<h2>Complete Subscription</h2>
									{selectedPlan.name
										.toLowerCase()
										.includes('storage') ? (
										// For storage add-ons, use the regular PaymentForm
										<PaymentForm
											amount={selectedPlan.price}
											planName={selectedPlan.name}
											planId={selectedPlan.id}
											onPaymentSuccess={() => {
												// This won't be called directly anymore since we're redirecting to Stripe Checkout
												// The success will be handled via the URL parameter when returning from Stripe
											}}
										/>
									) : (
										// For plan changes, show a confirmation button that calls handleUpdateSubscription
										<div className='update-confirmation'>
											<p>
												You are changing your
												subscription to:{' '}
												<strong>
													{selectedPlan.name}
												</strong>
											</p>
											<p>
												Price:{' '}
												<strong>
													$
													{selectedPlan.price.toFixed(
														2
													)}
													/
													{billingInterval ===
													'yearly'
														? 'year'
														: 'month'}
												</strong>
											</p>

											<div className='form-actions'>
												<button
													className='secondary-button'
													onClick={() =>
														setSelectedPlan(null)
													}>
													Cancel
												</button>
												<button
													className='primary-button'
													onClick={
														handleUpdateSubscription
													}
													disabled={loading}
													style={{
														backgroundColor:
															orgData?.secondaryColor ||
															'#333',
													}}>
													{loading
														? 'Processing...'
														: 'Confirm Subscription Change'}
												</button>
											</div>
										</div>
									)}
								</div>
							)}

							{showCart && cartItems.length > 0 && (
								<div className='cart-section detail-card'>
									<h2>Review Your Changes</h2>

									<div className='cart-items'>
										{cartItems.map((item) => (
											<div
												key={item.id}
												className='cart-item'>
												<div className='cart-item-details'>
													<h3>{item.name}</h3>
													<p className='cart-item-price'>
														${item.price.toFixed(2)}
														/
														{item.type ===
															'addon' &&
														item.billingInterval
															? item.billingInterval
															: billingInterval ===
															  'yearly'
															? 'year'
															: 'month'}
													</p>
												</div>
												<button
													className='remove-item-btn'
													onClick={() =>
														removeFromCart(item.id)
													}
													aria-label='Remove item'>
													<span className='material-icons'>
														close
													</span>
												</button>
											</div>
										))}
									</div>

									<div className='cart-total'>
										<p>
											Total: $
											{cartItems
												.reduce(
													(sum, item) =>
														sum + item.price,
													0
												)
												.toFixed(2)}
											/
											{billingInterval === 'yearly'
												? 'year'
												: 'month'}
										</p>
									</div>

									<div className='cart-actions'>
										<button
											className='secondary-button'
											onClick={() => {
												setCartItems([]);
												setShowCart(false);
											}}>
											Cancel
										</button>
										<button
											className='primary-button'
											onClick={handleUpdateSubscription}
											disabled={loading}
											style={{
												backgroundColor:
													orgData?.secondaryColor ||
													'#333',
											}}>
											{loading
												? 'Processing...'
												: 'Confirm Changes'}
										</button>
									</div>
								</div>
							)}

							{addonSuccess && (
								<div className='success-message'>
									<p>
										Add-on has been successfully added to
										your subscription!
									</p>
								</div>
							)}

							{showAddonConfirm && selectedAddon && (
								<div className='modal-overlay'>
									<div className='modal-content'>
										<h3>
											Add {selectedAddon.name} to your
											subscription
										</h3>
										<p>
											Would you like to be charged now or
											at your next billing cycle?
										</p>

										<div className='modal-actions'>
											<button
												className='secondary-button'
												onClick={() => {
													handleAddonSelect(
														selectedAddon,
														false
													);
													setShowAddonConfirm(false);
												}}
												disabled={loading}>
												Next Billing Cycle
											</button>
											<button
												className='primary-button'
												onClick={() => {
													handleAddonSelect(
														selectedAddon,
														true
													);
													setShowAddonConfirm(false);
												}}
												disabled={loading}
												style={{
													backgroundColor:
														orgData?.secondaryColor ||
														'#333',
												}}>
												Charge Now
											</button>
										</div>

										<button
											className='close-button'
											onClick={() =>
												setShowAddonConfirm(false)
											}>
											Cancel
										</button>
									</div>
								</div>
							)}
						</>
					)}
				</div>
			</div>
		</DashboardLayout>
	);
}

export default DashboardSubscription;
